import { Form, Input, Select, Space } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";
import MaskInput from "../mask-input";
import "./PhoneWithDDD.scss";
import {
  BrazilCountryCode,
  USCountryCode,
  CanadaCountryCode,
} from "shared/helpers/phone-helpers";

const BrazilMask = "(99) 99999-9999";
const UsCanadaMask = "(999) 999-9999";
const DefaultMask = "999999??????";

interface PhoneWithDDDProps {
  names: { phoneCountry: string; phone: string; ddi: string };
  rules?: { ddi?: any[]; phone?: any[] };
}

const PhoneWithDDD: React.FC<PhoneWithDDDProps> = ({
  names,
  rules = { phone: [], ddi: [] },
}) => {
  const { t } = useTranslation();
  const { countryOptions } = useSelector((state: any) => state.countries);
  const form = Form.useFormInstance();
  const [phoneMask, setPhoneMask] = useState<any>(DefaultMask);
  const countryCodePhone = Form.useWatch(names.phoneCountry, form);
  const data = countryOptions
    .filter((x: any) => x.ddi != null)
    .map((x: any) => {
      return {
        ...x,
        label: `${x.label} (${x.ddi})`,
      };
    });

  const handleDDDChange = useCallback((value: any) => {
    switch (value) {
      case BrazilCountryCode:
        setPhoneMask(BrazilMask);
        break;
      case USCountryCode:
      case CanadaCountryCode:
        setPhoneMask(UsCanadaMask);
        break;
      default:
        setPhoneMask(DefaultMask);
    }
  }, []);

  useEffect(() => {
    handleDDDChange(countryCodePhone);
  }, [countryCodePhone, handleDDDChange]);

  const handleFieldChange = () => {
    const dddValue = form.getFieldValue(names.phoneCountry);
    const phoneValue = form.getFieldValue(names.phone);

    if (dddValue && !phoneValue) {
      form.setFields([
        {
          name: names.phone,
          errors: [t("quiz.pages.Questionario.phoneRequired")],
        },
      ]);
    } else if (!dddValue && phoneValue) {
      form.setFields([
        {
          name: names.phoneCountry,
          errors: [t("quiz.pages.Questionario.dddRequired")],
        },
      ]);
    } else {
      form.setFields([
        { name: names.phoneCountry, errors: [] },
        { name: names.phone, errors: [] },
      ]);
    }
  };

  const validateConditionalFieldRequirement = (
    fieldValue: any,
    anotherFieldName: string,
    errorMessage: string
  ) => {
    if (
      !fieldValue &&
      form.getFieldValue(anotherFieldName) &&
      form.getFieldValue(anotherFieldName).replace(/\s+/g, "").length > 0
    ) {
      return Promise.reject(new Error(t(errorMessage)));
    }
    return Promise.resolve();
  };

  const onSelectDdiChange = (value: any, opt: any) => {
    if (!value) return form.setFieldValue(names.ddi, null);

    handleDDDChange(value);
    handleFieldChange();
    form.setFieldValue(names.ddi, opt.ddi);
  };

  return (
    <Space.Compact className={`wrapper-group-phone ${isMobile && "mobile"}`}>
      <Form.Item name={names.ddi} style={{ display: "none" }}>
        <Input type="text" />
      </Form.Item>
      <Form.Item
        className={`input-ddd ${isMobile && "mobile"}`}
        name={[names.phoneCountry]}
        validateFirst
        rules={[
          ...(rules.ddi || []),
          {
            validator: (_, value) => {
              return validateConditionalFieldRequirement(
                value,
                names.phone,
                "quiz.pages.Questionario.dddRequired"
              );
            },
          },
        ]}
      >
        <Select
          allowClear
          showSearch
          optionLabelProp="ddi"
          options={data}
          notFoundContent={t("quiz.pages.Questionario.noItem")}
          placeholder={t("quiz.pages.Questionario.ddd")}
          filterOption={filterOptionDiacritics}
          onChange={onSelectDdiChange}
        />
      </Form.Item>
      <Form.Item
        className={`input-phone ${isMobile && "mobile"}`}
        name={names.phone}
        validateFirst
        rules={[
          ...(rules.phone || []),
          {
            validator: (_, value) => {
              return validateConditionalFieldRequirement(
                value,
                names.phoneCountry,
                "quiz.pages.Questionario.dddRequired"
              );
            },
          },
        ]}
      >
        <MaskInput
          mask={phoneMask}
          placeholder={t("quiz.pages.Questionario.phoneLabel")}
          onChange={handleFieldChange}
          formatChars={{ 9: "[0-9]", "?": "[0-9 ]" }}
        />
      </Form.Item>
    </Space.Compact>
  );
};

export default PhoneWithDDD;
